"use client"

import styled from "@emotion/styled"
import { IconButton } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { NewText, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import { RentProjectFAQ, RentProjectFAQItem } from "utils/projects"
// cubic-bezier(0.4, 0, 0.2, 1)
const ANIMATION_TIMING_FUNCTION = "cubic-bezier(.87,.27,.72,1.11)"
const ACCORDION_HEIGHT = 75

const StyledContainer = styled.div`
  padding-block: 60px;
  padding-inline: 20px;
  padding-bottom: 100px;
  max-width: 1240px;
  margin: 0 auto;
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-bottom: 60px;
  }
`
const StyledFaq = styled.section`
  --faq-black: #011727;
  color: var(--faq-black);
  overflow: hidden;
  width: 100%;
  position: relative;
  &::after {
    background-image: url("/images/rent-faq-bg.png");
    background-repeat: no-repeat;
    background-size: contain;
    position: absolute;
    z-index: -1;
    bottom: -220px;
    width: 590px;
    height: 715px;
    inset-inline-end: 30px;
    ${(props) => props.theme.breakpoints.up("md")} {
      content: "";
    }
  }
`

const StyledHeadings = styled.div`
  text-align: center;
  margin-bottom: 32px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 64px;
    text-align: unset;
  }
`

const StyledH4 = styled(NewText)`
  margin: 0;
  margin-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 16px;
  }
`

const StyledP = styled(NewText)`
  margin: 0;
`

const FAQList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 856px;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: flex-start;
  }
`

const StyledFAQListItem = styled.li<{ toggled: boolean }>`
  --opened-height: auto;
  overflow: hidden;
  width: 100%;
  height: ${(props) => (props.toggled ? "var(--opened-height)" : `${ACCORDION_HEIGHT}px`)};
  background-color: white;
  border-radius: 12px;
  border: 1px solid ${(props) => props.theme.colors.bulletsBackgroundColor4rentMarketing};
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  transition: 0.6s all ${ANIMATION_TIMING_FUNCTION};

  &:hover {
    border-color: ${(props) => props.theme.colors.blue4rentMarketing};
  }
`
const StyledFAQListItemHeader = styled.div`
  flex-shrink: 0;
  height: ${ACCORDION_HEIGHT + 1}px;
  width: 100%;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`
const StyledFAQListItemContent = styled(NewText)`
  margin: 20px;
  margin-top: 0;
  padding-top: 20px;
  flex-shrink: 0;
  border-top: 1px solid ${(props) => props.theme.colors.bulletsBackgroundColor4rentMarketing};
`

const CustomPlusIcon = styled.div<{ toggled?: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: ${(props) => props.theme.colors.blue4rentMarketing};
    border-radius: 50%;
    z-index: 2;
    opacity: 1;
    transition: 0.6s all ${ANIMATION_TIMING_FUNCTION};
  }

  &::before {
    width: 2px;
    height: 12px;
    transform: translate(-50%, -50%) rotate(${(props) => (props.toggled ? "90deg" : "0deg")});
  }

  &::after {
    height: 2px;
    width: 12px;
    opacity: ${(props) => (props.toggled ? 0 : 1)};
  }
`
type FAQListItemProp = {
  item: RentProjectFAQItem
  inView: boolean
  toggled: boolean
  handleToggle: () => void
}

const FAQListItem = ({ item, inView, toggled, handleToggle }: FAQListItemProp) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [calculatedHeight, setCalculatedHeight] = useState<number | undefined>(undefined)

  const title = item?.accordionTitle
  const content = item?.accordionContent

  useEffect(() => {
    if (!inView) return
    const calculateHeight = () => {
      if (contentRef?.current) {
        const result = contentRef.current.scrollHeight + 1 + ACCORDION_HEIGHT
        setCalculatedHeight(result)
      }
    }

    // Initial calculation after a delay to ensure dangerouslySetInnerHTML content is rendered
    const timeoutId = setTimeout(calculateHeight, 100)

    // Set up ResizeObserver to recalculate if content changes (with fallback)
    let resizeObserver: ResizeObserver | null = null
    if (contentRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        calculateHeight()
      })
      resizeObserver.observe(contentRef.current)
    }

    return () => {
      clearTimeout(timeoutId)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [title, content, inView])

  return !title || !content ? null : (
    <StyledFAQListItem
      toggled={toggled}
      style={
        {
          "--opened-height": calculatedHeight ? `${calculatedHeight}px` : "auto",
        } as React.CSSProperties
      }
    >
      <StyledFAQListItemHeader className="styledFAQListItemHeader">
        <NewText
          component="h6"
          mobileFontSize={18}
          desktopFontSize={18}
          desktopFontWeight={300}
          lineHeight={"150%"}
          letterSpacing={"-0.36px"}
          color="inherit"
        >
          {title}
        </NewText>
        <IconButton onClick={handleToggle}>
          <CustomPlusIcon toggled={toggled} />
        </IconButton>
      </StyledFAQListItemHeader>
      <div ref={contentRef}>
        <StyledFAQListItemContent
          component="div"
          desktopFontSize={16}
          fontWeight={300}
          color="inherit"
          desktopLineHeight={"150%"}
          mobileLineHeight={"145%"}
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      </div>
    </StyledFAQListItem>
  )
}

type Props = {
  data: RentProjectFAQ
  locale: string
}

const RentFAQ = ({ data }: Props) => {
  const [activeFAQIndex, setActiveFAQIndex] = useState<number>(0)
  const { ref, inView } = useInView(useInViewConfig)
  const blockTitle = data?.blockTitle
  const subTitle = data?.subTitle
  const items = data?.items

  const handleToggle = (index: number) => {
    setActiveFAQIndex((prevIndex) => (prevIndex === index ? -1 : index))
  }

  let faqSchema = null
  if (items && Array.isArray(items) && items.length > 0) {
    faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((item: any) => ({
        "@type": "Question",
        name: item.accordionTitle || item.title || "",
        acceptedAnswer: {
          "@type": "Answer",
          text: item.accordionContent || item.text || "",
        },
      })),
    }
  }

  return (
    <>
      {faqSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <TransitionUp ref={ref} $inView={inView} $delay={transitionDelayTime}>
        <StyledFaq>
          <StyledContainer>
            <StyledHeadings>
              {blockTitle && (
                <StyledH4
                  component="h4"
                  desktopFontSize={60}
                  mobileFontSize={40}
                  desktopLineHeight={"150%"}
                  desktopFontWeight={700}
                  color="inherit"
                  letterSpacing={"-0.4px"}
                  fontFamily={"var(--narkis-condensed) !important"}
                >
                  {blockTitle}
                </StyledH4>
              )}
              {subTitle && (
                <StyledP
                  component="p"
                  desktopFontSize={24}
                  mobileFontSize={20}
                  desktopLineHeight={"150%"}
                  mobileLineHeight={"130%"}
                  desktopFontWeight={300}
                  color="inherit"
                  letterSpacing={"-0.48px"}
                >
                  {subTitle}
                </StyledP>
              )}
            </StyledHeadings>
            <FAQList>
              {items?.map((item, index) => (
                <FAQListItem
                  key={index}
                  item={item}
                  inView={inView}
                  toggled={activeFAQIndex === index}
                  handleToggle={() => handleToggle(index)}
                />
              ))}
            </FAQList>
          </StyledContainer>
        </StyledFaq>
      </TransitionUp>
    </>
  )
}
export default RentFAQ
